import { AbstractRenderer, Container, Graphics, RenderTexture, Sprite, Texture } from "pixi.js"

/** Pixel coordinates on the existing 32x64 rock face. Dark rims keep flecks legible in cave light. */
export const ORE_FLECK_STYLES = {
    coal: {
        color: 0x242630, shadow: 0x121319, highlight: 0x55545c,
        flecks: [[8, 12, 4, 3], [20, 19, 4, 3], [13, 27, 3, 3], [7, 39, 4, 3], [21, 47, 3, 3]],
    },
    iron: {
        color: 0x947969, shadow: 0x493b34, highlight: 0xc6aa91,
        flecks: [[9, 12, 4, 2], [20, 19, 3, 2], [12, 27, 4, 2], [8, 40, 3, 2], [21, 47, 4, 2]],
    },
    copper: {
        color: 0xc47946, shadow: 0x603e2b, highlight: 0xe7aa67,
        flecks: [[9, 12, 3, 3], [20, 20, 3, 3], [12, 27, 3, 3], [8, 40, 3, 3], [21, 47, 3, 3]],
    },
    silver: {
        color: 0xb6c8d0, shadow: 0x526577, highlight: 0xe8f3f5,
        flecks: [[10, 11, 2, 4], [21, 19, 2, 4], [13, 26, 2, 4], [9, 39, 2, 4], [22, 46, 2, 4]],
    },
    gold: {
        color: 0xd5a332, shadow: 0x705019, highlight: 0xf6d46a,
        flecks: [[9, 12, 3, 2], [20, 20, 3, 2], [12, 27, 3, 2], [8, 40, 3, 2], [21, 47, 3, 2]],
    },
} as const
export type OreMaterial = keyof typeof ORE_FLECK_STYLES
export const isOreMaterial = (value: unknown): value is OreMaterial =>
    typeof value === "string" && Object.prototype.hasOwnProperty.call(ORE_FLECK_STYLES, value)

const textures = new WeakMap<Texture, Map<OreMaterial, Texture>>()
/** Bake once per material and base texture; the normal sprite owns cracks, fading and destruction. */
export const oreMineralTexture = (base: Texture, material: OreMaterial, renderer: AbstractRenderer): Texture => {
    let variants = textures.get(base)
    if (!variants) { variants = new Map(); textures.set(base, variants) }
    const cached = variants.get(material)
    if (cached?.orig && !cached.baseTexture?.destroyed) return cached
    const surface = new Container()
    surface.addChild(new Sprite(base))
    const flecks = new Graphics()
    const style = ORE_FLECK_STYLES[material]
    for (const [x, y, width, height] of style.flecks) {
        flecks.beginFill(style.shadow).drawRect(x - 1, y - 1, width + 2, height + 2).endFill()
        flecks.beginFill(style.color).drawRect(x, y, width, height).endFill()
        flecks.beginFill(style.highlight).drawRect(x, y, Math.max(1, width - 1), 1).endFill()
    }
    surface.addChild(flecks)
    const texture = RenderTexture.create({ width: base.width, height: base.height, scaleMode: base.baseTexture.scaleMode })
    renderer.render(surface, { renderTexture: texture, clear: true })
    surface.destroy({ children: true })
    variants.set(material, texture)
    return texture
}
